import React, { useState, useEffect, useMemo } from 'react';
import classnames from 'classnames';
import { useIntl, useSelector, getLocale, history } from 'umi';
import { Card, Justify, PagingQuery } from 'tea-component';
import Loading from '@/pages/intelligence/components/Loading';
import CardSlot from '@/pages/intelligence/components/CardSlot';
import SearchRecent from '@/pages/intelligence/components/SearchRecent';
import ExportTable from '@/pages/intelligence/components/ExportTable';
import ChartIconType from '@/pages/intelligence/components/ChartsComponents/ChartIconType';
import Filter from '@/pages/intelligence/pages/TopCharts/components/CommonFilter';
import Visualis from './components/Visualis';
import Tables from './components/Tables';
import CompanyUserGuide, {
  COMPANY_GUIDE_CLASSNAMES,
} from '@intelligence/components/UserGuide/Company';
import useTopChartsCompaniesCoreModel from './hooks/useTopChartsCompaniesCoreModel';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import { fetchTopChartsCompaniesRanksSearch, getSpecialArrValue } from './services';
import { IPO_STATUSES } from '@/pages/intelligence/components/TeaSelector/IPOStatusSelector';
import { ChartType, SortMode } from '@/pages/intelligence/pages/TopCharts/_config/enums';
import {
  namespace as USER_GUIDE_NAMESPACE,
  GUIDE_TYPE_INTELLIGENCE_COMPANY,
} from '@home/models/guide';
import { FILTER_EXTEND_NAMESPACE } from '@/pages/intelligence/models/filterExtend';
import {
  TOP_COMPANIES_METRICS_MAPPING,
  TOP_COMPANIES_DATASOURCES_MAPPING,
} from '@/pages/intelligence/pages/TopCharts/_config/topChartsConst';
import {
  TOP_CHARTS_TYPE_NAMES_MAPPING,
  DEFAULT_GAME_TYPES_MAPPING,
  COLUMNS_SORT_METADATAS_MAPPING,
  PAGE_LOG_CONFIG_KEYS_MAPPING,
  PAGE_DOWNLOAD_FORM_CONFIG_KEYS_MAPPING,
  DOWNLOAD_GAMES_TYPES_MAPPING,
} from './constants';
import type { IBaseProps } from '@/pages/intelligence/types/common';
import type { SortByItemType } from '@/pages/intelligence/components/TableComponents/components/SortBy';
import type {
  TableSelectStatusType,
  TableSelectionType,
} from '@/pages/intelligence/components/TableComponents/components/TableCheckbox';
import type {
  ITopChartsCompaniesBaseProps,
  ITopChartsCompaniesFilter,
  ITopChartsCompaniesFetchParams,
  ITopChartsCompaniesRank,
} from './types';
import ArrowdownText from '@/pages/intelligence/components/ArrowdownText';
import './index.scss';
import { getFilterName, getFilterValueStorage } from '@/pages/intelligence/components/Filter/util';
import { getDbiFilterParams } from '@intelligence/utils/filterUtils';
import { getCustomExtend } from '@intelligence/pages/TopCharts/Companies/components/CompaniesCore/utils';
import {
  URL_CHART_TYPE,
  updateShortLinkPath,
  URL_SHORT_LINK_NAME,
  URL_SORT_NAME,
  URL_ORDER,
  URL_RATIO_TYPE,
} from '@intelligence/utils/urlPathCfg';

const ENTITY_TYPE = 'company';

interface ITopChartsCompaniesCoreProps extends IBaseProps, ITopChartsCompaniesBaseProps {}

export default function CompaniesCore(props: ITopChartsCompaniesCoreProps) {
  const {
    topChartsType,
    pageLogInfo,
    filterCfg,
    headQuatersOptionsData,
    rangeTimeComponentDefault,
    filterLoading,
    shortLinkValidQuery = {},
    shortLinkFilterValue,
    urlCustomParams,
  } = props;
  const DEFAULT_FORM_VALUE = filterCfg?.defaultValue;

  const isRevenue = topChartsType === 'top_companies_revenue';
  const isDownload = topChartsType === 'top_companies_downloads';
  const isMentions = topChartsType === 'top_companies_mentions';
  const defaultGameTypes = DEFAULT_GAME_TYPES_MAPPING[topChartsType] ?? ['mobile'];
  const { userInfo } = useSelector((state: any) => {
    const { userInfo } = state.home;
    return {
      userInfo,
    };
  });
  const filterSaveName = getFilterName(userInfo);
  const filterSaveData = getFilterValueStorage(userInfo);

  const { filterState } = useSelector((state: any) => ({
    filterState: state?.[FILTER_EXTEND_NAMESPACE],
  }));

  const { guideType } = useSelector((state: any) => {
    return state?.[USER_GUIDE_NAMESPACE] || {};
  });
  const isCompanyUserGuideStepMode = guideType === GUIDE_TYPE_INTELLIGENCE_COMPANY;

  const { formatMessage } = useIntl(); // 中英切换方法
  const locale = getLocale();
  const titleNow = TOP_CHARTS_TYPE_NAMES_MAPPING[topChartsType];
  const translatedTitleNow = formatMessage({ id: titleNow });

  const { filterExtend, setFilterExtend } = useFilterExtend({});

  const isOperate = filterState?.isOperate;
  const [filterValue, setFilterVale] = useState(
    isOperate
      ? getCustomExtend(filterExtend, isRevenue || isDownload, isMentions).filter
      : DEFAULT_FORM_VALUE,
  );

  const [isError, setIsError] = useState(false);

  /** ******** 榜单核心逻辑hooks，该hooks内处理了，页面初始化查询、排序、翻页等关联逻辑，begin***************/
  const {
    tableTotal,
    tableData,
    chartType,
    setChartType,
    barOrPieData,
    setBarOrPieData,
    lineData,
    fetchParams,
    getGameData,
    getLineChartData,
    defaultSelectionGames,
    isNewComfirm,
    setIsNewComfirm,
    isNewCheckGame,
    setIsNewCheckGame,
    tableSelection,
    operatedGame,
    setOperatedGame,
    setTableSelection,
    isNodata,
    handleClearSearch,
    transTableDataForSearch,
  } = useTopChartsCompaniesCoreModel({
    topChartsType,
    headQuatersOptions: headQuatersOptionsData,
    filterValue,
    setFilterVale,
    hasUrlShortLink: filterCfg?.hasUrlShortLink,
  });
  /** ****** 榜单核心逻辑hooks，该hooks内处理了，页面初始化查询、排序、翻页等关联逻辑 end *******************/

  const downloadFetchParams = (() => {
    const pageMetrics = TOP_COMPANIES_METRICS_MAPPING[topChartsType];
    return {
      entity_type: ENTITY_TYPE,
      metrics: pageMetrics?.length ? pageMetrics : ['revenue'],
      // 跟后端约定，region_type 固定按market走，完全不要region模式，所以这里固定传死market
      region_type: 'market',
      regions: fetchParams?.regions ?? [],
      data_sources: isMentions
        ? ['rention']
        : [
            fetchParams.datasource_sim ||
              filterValue.dataSourceSingle?.value ||
              DEFAULT_FORM_VALUE.dataSourceSingle?.value,
          ],
      date_method: 'aggregated',
      start_date: fetchParams?.start_date ?? '',
      end_date: fetchParams?.end_date ?? '',
      platforms: ['unified'],
      order: fetchParams?.order ?? 'desc',
      order_source:
        fetchParams.datasource_sim ||
        filterValue.dataSourceSingle?.value ||
        DEFAULT_FORM_VALUE.dataSourceSingle?.value,
      order_metric: isMentions ? 'mention' : fetchParams?.order_metric ?? 'total_revenue',
      ratio_type: fetchParams?.ratio_type ?? 0,
      page: 1,
      page_size: Number(tableTotal),
      granularity: fetchParams?.granularity ?? 'monthly',
      aggregate: '1',
      Listed: fetchParams?.listed ?? IPO_STATUSES,
      genre: isRevenue ? [] : fetchParams?.genre ?? [],
      sub_genre: isRevenue ? [] : fetchParams?.sub_genre ?? [],
      company_ids: [],
      // ratio_type: 0,
      company_headquarter: getSpecialArrValue(
        fetchParams?.company_headquarter ?? [],
        headQuatersOptionsData,
      ),
      game_type:
        fetchParams?.game_type?.map((item) => DOWNLOAD_GAMES_TYPES_MAPPING[item]) ??
        defaultGameTypes,
      // date_method: 'aggregated',
      // download_metrics: [
      //   'mobile_revenue',
      //   'pc_physical_revenue',
      //   'pc_digital_revenue',
      //   'console_digital_revenue',
      //   'console_physical_revenue',
      // ],
      // games_owned_show: 1,
      // ipo_status_show: 0,
      // headquaters_show: 0,
      // downlaod_date_method: 'aggregated',
      // region_method: 'aggregated',
      // ratio_show: 1,
      // file_format: 0,
      download_segments: [],
      download_metric_type: pageMetrics?.[0] ?? 'revenue',
      file_name: '',
      revenue_range_unit: fetchParams?.revenue_range_unit ?? {},
    };
  })();

  const [tableLoading, setTableLoading] = useState(true);
  const [isVisualisLoading, setIsVisualisLoading] = useState(true);

  // 将短链接配置更新到页面中
  useEffect(() => {
    if (shortLinkValidQuery?.[URL_CHART_TYPE] !== undefined) {
      setChartType(shortLinkValidQuery?.[URL_CHART_TYPE]);
    }
  }, [shortLinkValidQuery]);

  // 操作页面内容更新到短链接
  useEffect(() => {
    if (!filterLoading && filterCfg?.hasUrlShortLink) {
      updateShortLinkPath({
        shortLinkQuery: {
          [URL_CHART_TYPE]: chartType,
          [URL_SORT_NAME]: fetchParams.order_metric,
          [URL_ORDER]: fetchParams.order,
          [URL_RATIO_TYPE]: fetchParams.ratio_type,
        },
      });
    }
  }, [
    filterLoading,
    chartType,
    fetchParams.order_metric,
    fetchParams.order,
    fetchParams.ratio_type,
  ]);

  useEffect(() => {
    if (filterLoading || !userInfo?.userid) return;
    setTableLoading(true);
    setIsVisualisLoading(true);

    const hasUrlShortLinkVal = history.location?.query?.[URL_SHORT_LINK_NAME];
    // 处理短链接、保存、继承模式优先级，获得筛选器值和对应的参数
    const { filterValue: extendFilter, filterParams } = getDbiFilterParams({
      userInfo,
      urlCustomParams,
      urlShortLinkFilter: shortLinkFilterValue,
      filterSaveData: filterSaveData?.apiValue,
      filterExtend,
      specialParams: filterCfg?.specialParams,
      isOperate,
      filterName: filterSaveName,
      filterKeyList: filterCfg?.filterKeyList ?? Object.keys(DEFAULT_FORM_VALUE),
    });

    // 特殊处理时间年粒度继承问题
    const { filter: newFilterValues, params } = getCustomExtend(
      extendFilter,
      isRevenue || isDownload,
      isMentions,
    );
    // console.log('newFilterValues', newFilterValues);
    // console.log('paramsparams', params);

    // 如果有短链接，设置为继承模式
    if (isOperate || hasUrlShortLinkVal) {
      // 如果目前是继承模式或url短链接模式，就设置继承值为过滤后的筛选器值
      setFilterExtend(newFilterValues);
    }
    // 更新筛选器值
    setFilterVale(newFilterValues);

    // 更新url,保存、继承处理后得到的最新参数
    const newFetchParams = {
      ...fetchParams,
      ...(urlCustomParams || {}),
      // 页面自定义处理参数函数
      ...filterCfg?.handleFilterToApiParams?.({ ...(filterParams || {}), ...params }),
      data_sources: [
        (filterCfg?.handleFilterToApiParams?.({ ...(filterParams || {}), ...params })
          ?.datasource_sim || filterValue.dataSourceSingle?.value) === 'sensortower'
          ? 'company_sensortower'
          : 'appannie',
        ...TOP_COMPANIES_DATASOURCES_MAPPING[topChartsType],
      ],
      ...params,
    };
    // 更新后的参数获取页面接口数据
    getData({
      params: newFetchParams,
      isCheckGame: operatedGame,
      isUpdateLineChart: true,
      updateFilterValues: newFilterValues,
    });
  }, [filterLoading, userInfo?.userid, shortLinkFilterValue]);

  const getData = async (args: {
    params: any;
    isCheckGame: boolean;
    isSearch?: boolean;
    isUpdateLineChart: boolean;
    // 更新后的筛选器值
    updateFilterValues?: any;
    // 保存方案MyFilter的值
    myFilterValue?: string;
  }) => {
    const {
      params,
      isCheckGame,
      isSearch = false,
      isUpdateLineChart = true,
      updateFilterValues = filterValue,
      myFilterValue = filterSaveName,
    } = args;
    const headQuaters = params?.company_headquarter;
    const paramsTmp = {
      ...params,
      game_type: isRevenue ? params?.game_type ?? defaultGameTypes : ['mobile'],
      // 跟后端约定，region_type 固定按market走，完全不要region模式，所以这里固定传死market
      region_type: 'market',
      regions: params?.regions ?? ['global'],
      genre: isRevenue ? [] : params?.genre ?? [],
      sub_genre: isRevenue ? [] : params?.sub_genre ?? [],
      listed: params?.listed ?? IPO_STATUSES,
      company_headquarter: headQuaters ?? [],
      company_ids: isSearch ? params?.company_ids : [],
    };
    try {
      const res = await getGameData({
        params: paramsTmp,
        isCheckOrSearch: isCheckGame,
        // isUpdateLineChart,
        updateFilterValues,
        filterSaveName: myFilterValue || '',
      });
      setTableLoading(false);
      if (isUpdateLineChart && chartType === ChartType.Line) {
        const { fetchParams, tableSelection } = res || {};
        const ids = tableSelection?.map((item: ITopChartsCompaniesRank) => item?.app_id) || [];
        getTrend(fetchParams, ids);
      } else {
        setIsVisualisLoading(false);
      }
    } catch (err) {
      setTableLoading(false);
      setIsVisualisLoading(false);
      setIsError(true);
    }
  };

  const getTrend = (params: any, idList: string[]) => {
    setIsVisualisLoading(true);
    getLineChartData(params, idList)
      .then(() => {
        setIsVisualisLoading(false);
      })
      .catch(() => {
        setIsVisualisLoading(false);
        setIsError(true);
      });
  };

  const handleConfirm = async (
    value: ITopChartsCompaniesFilter,
    filter: any,
    myFilterValue: string,
  ): Promise<void> => {
    const fetchParamsTmp: ITopChartsCompaniesFetchParams = {
      ...fetchParams,
      ...value,
      data_sources: [
        value.datasource_sim === 'sensortower' ? 'company_sensortower' : 'appannie',
        ...TOP_COMPANIES_DATASOURCES_MAPPING[topChartsType],
      ],
      page: 1,
      page_size: 100,
    };
    // 请求接口
    setTableLoading(true);
    setIsVisualisLoading(true);
    /** 修改筛选器后，游戏应该重置为未勾选状态 */
    setOperatedGame(false);
    await getData({
      params: fetchParamsTmp,
      isCheckGame: false,
      isSearch: false,
      isUpdateLineChart: true,
      updateFilterValues: filter,
      myFilterValue,
    });
    setIsNewComfirm(true);
    setIsNewCheckGame(true);
  };

  // 切换charts类型事件
  const handleChartType = (value: ChartType) => {
    if (value === ChartType.Line && tableSelection?.length && isNewComfirm && isNewCheckGame) {
      const idList = tableSelection.map((t) => t?.app_id);
      getTrend(fetchParams, idList);
    }
    setChartType(value);
  };

  const onTableSortChage = async (e: SortByItemType) => {
    const fetchParamsTmp: ITopChartsCompaniesFetchParams = {
      ...fetchParams,
      order_source: e?.orderSource || '',
      order_metric: e?.sortMetric ?? '',
      // @ts-ignore
      order: e?.sortVal,
      ratio_type: e?.ratioType ?? 0,
      page: 1,
      page_size: 100,
    };
    setTableLoading(true);
    if (!operatedGame) {
      /** 没有勾选时点排序则重新Loading柱状图数据 */
      setIsVisualisLoading(true);
    }
    await getData({
      params: fetchParamsTmp,
      isCheckGame: operatedGame,
      isSearch: false,
      isUpdateLineChart: !operatedGame,
    });
  };

  const onTablePagingChange = (e: PagingQuery) => {
    const fetchParamsTmp: ITopChartsCompaniesFetchParams = {
      ...fetchParams,
      page: e?.pageIndex || 1,
      page_size: e?.pageSize || 100,
    };
    setTableLoading(true);
    getData({
      params: fetchParamsTmp,
      isCheckGame: operatedGame,
      isSearch: false,
      isUpdateLineChart: false,
    });
  };

  // 表格列表项选择事件回调
  const onTableCheckGame = (
    selectList: TableSelectionType[],
    checkStatus: TableSelectStatusType,
  ) => {
    setIsNewComfirm(true);
    setIsNewCheckGame(true);

    const idList = selectList.map((t) => t?.app_id);
    if (chartType === ChartType.Line) {
      getTrend(fetchParams, idList);
    }
    /** 是否勾选了游戏 */
    if (checkStatus === 'cancel' || checkStatus === 'all' || selectList.length === 0) {
      setOperatedGame(false);
    } else {
      setOperatedGame(true);
    }
    // @ts-ignore
    setBarOrPieData(selectList);
    setTableSelection(selectList);
  };

  const handleReset = () => {
    setOperatedGame(false);
  };

  const getTableSearchList = (id: string) => {
    setTableLoading(true);
    getData({
      params: { ...fetchParams, company_ids: [id] },
      isCheckGame: true,
      isSearch: true,
      isUpdateLineChart: false,
    });
  };

  const orderNameInfo = useMemo<string>(() => {
    if (!fetchParams) return '';
    const { order_metric: orderMetric, ratio_type: ratioType } = fetchParams;
    const orderMetricInfo = Object.values(COLUMNS_SORT_METADATAS_MAPPING).find(
      (item: any) => item?.key === orderMetric,
    );
    const result = orderMetricInfo?.title ? formatMessage({ id: orderMetricInfo?.title }) : '';
    return ratioType === SortMode.Circle ? `${result}(%)` : result;
  }, [locale, fetchParams?.order_metric, fetchParams?.ratio_type]);

  return (
    <>
      <Card>
        <Card.Body>
          <Filter
            defaultValue={DEFAULT_FORM_VALUE}
            filterCfg={filterCfg}
            timeFilterValues={{
              type: fetchParams.granularity,
              value: [fetchParams.start_date, fetchParams.end_date],
            }}
            filterConfirm={handleConfirm}
            filterReset={handleReset}
            rangeTimeComponentDefault={rangeTimeComponentDefault}
            filterValue={filterValue}
            setFilterVale={setFilterVale}
          />
        </Card.Body>
      </Card>
      <Card className="dbi-mobileGames-card-context dbi-top-companies__visualis-card">
        <Card.Body
          title={
            <div style={{ display: 'flex' }}>
              <span style={{ marginRight: 10 }}>{translatedTitleNow}</span>
              <ArrowdownText isNew={true} sortedData={orderNameInfo} order={fetchParams?.order} />
            </div>
          }
          operation={
            tableLoading ? null : (
              <ChartIconType chartType={chartType} handleChartType={handleChartType} />
            )
          }
        >
          {isVisualisLoading ? (
            <div style={{ height: 350 }}>
              <Loading />
            </div>
          ) : (
            <Visualis
              topChartsType={topChartsType}
              chartType={chartType}
              fetchParams={fetchParams}
              barOrPieData={barOrPieData}
              lineData={lineData}
              isVisualisLoading={isVisualisLoading}
              isError={isError}
              isNodata={isNodata}
              orderTypeInfo={fetchParams?.order}
              titleNow={translatedTitleNow}
              imageType={'mobile'}
            />
          )}
        </Card.Body>
      </Card>
      <Justify
        className="dbi-card-justify"
        right={
          <>
            {/* <SearchBox
                  className="card-search"
                  onSearch={console.log}
                  onClear={() => console.log('clear')}
                  onHelp={() => console.log('help')}
                /> */}
            <SearchRecent
              searchType={ENTITY_TYPE}
              placeholder={formatMessage(
                { id: 'dbi_table_search_placeholder' },
                { domainName: formatMessage({ id: 'dbi_company_company_lowercase' }) },
              )}
              fetchParams={fetchParams}
              handleClear={handleClearSearch}
              getTableResultList={getTableSearchList}
              searchListApi={(params: any, cfg: any) =>
                fetchTopChartsCompaniesRanksSearch(params, {
                  headQuatersOptions: headQuatersOptionsData,
                  signal: cfg?.signal || null,
                })
              }
              pageLogInfo={pageLogInfo}
              initSearchInput={!(fetchParams?.company_ids?.length > 0)}
              transTableDataForSearch={transTableDataForSearch}
            />
            <ExportTable
              classModalName="dbi-top-companies-download-modal"
              entityType={ENTITY_TYPE}
              downloadModalSize={820}
              // @ts-ignore
              businessType={PAGE_DOWNLOAD_FORM_CONFIG_KEYS_MAPPING[topChartsType]}
              isDisableDownload={tableLoading}
              downloadLogConfigKey={`${PAGE_LOG_CONFIG_KEYS_MAPPING[topChartsType]}DownloadExcel`}
              excelName={translatedTitleNow}
              total={tableTotal}
              isNeedCheckedLines={true}
              tableParams={{ ...downloadFetchParams, game_type: ['Mobile', 'PC', 'Console'] }}
              getCheckedLinesCfg={() => {
                const companiesIds = tableSelection?.map((item) => item?.app_id) || [];
                return { key: 'company_ids', value: companiesIds };
              }}
              dataSource={downloadFetchParams.order_source}
            />
          </>
        }
      />
      <CardSlot className="dbi-user-guide__top-companies__tables-card">
        <Tables
          entityType={ENTITY_TYPE}
          topChartsType={topChartsType}
          records={tableData}
          loading={tableLoading}
          tableTotal={tableTotal}
          tableSelection={tableSelection}
          tableSelectionAll={defaultSelectionGames}
          pageSortAttr={{
            currentOrderSource: fetchParams?.order_source, // 排序指标的数据源
            currentSortMetric: fetchParams?.order_metric, // 排序指标
            currentSortValue: fetchParams?.order, // 排序方式 asc、desc
            currentRatioType: fetchParams?.ratio_type, // 排序模式，正常数值、环比
            tablePageIndex: fetchParams?.page,
            tablePageSize: fetchParams?.page_size,
            dataSources: fetchParams?.data_sources,
            // currentSortMetric: `${fetchParams?.order_metric}_${fetchParams?.order_source}`,
          }}
          // @ts-ignore
          onTableSortChage={onTableSortChage}
          onTablePagingChange={onTablePagingChange}
          onTableCheckGame={onTableCheckGame}
          gameTypes={fetchParams?.game_type ?? defaultGameTypes}
        />
        {/* Company新手引导 第二步骤中 高亮表格中公司单元格 需要用到的dom */}
        <div
          className={classnames(COMPANY_GUIDE_CLASSNAMES.ELEMENT2, {
            show: isCompanyUserGuideStepMode,
          })}
        ></div>
        {/* Company新手引导 */}
        {tableLoading || <CompanyUserGuide />}
      </CardSlot>
    </>
  );
}
